import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useColorModeValue,
  HStack,
  Icon,
  VStack,
  Heading,
  Divider,
  SimpleGrid
} from '@chakra-ui/react';
import { FiAlertCircle, FiCheckCircle, FiSettings, FiSend, FiFilter, FiArrowRight } from 'react-icons/fi';
import { useNavigate, useOutletContext } from 'react-router-dom';

const mockQueueData = [
  {
    id: 'CLM-9921-A',
    patient: 'James Wilson',
    dos: '2026-06-24',
    emr: 'OCHIN Epic',
    billed: '$200.00',
    tier: 'Tier B',
    issue: 'Same-Day Visit (Duplicate T1015)',
    action: 'Needs Box 80 Justification',
    route: '/fq/demo/box-80'
  },
  {
    id: 'CLM-8832-B',
    patient: 'James Smith',
    dos: '2026-06-24',
    emr: 'eClinicalWorks',
    billed: '$180.00',
    tier: 'Tier C',
    issue: 'PCP Mismatch (Assigned: Dr. Lee)',
    action: 'Update Rendering Provider',
    route: null
  },
  {
    id: 'CLM-7741-C',
    patient: 'Linda Johnson',
    dos: '2026-06-23',
    emr: 'NextGen',
    billed: '$45.00',
    tier: 'Tier C',
    issue: 'Non-Qualifying PPS (99211)',
    action: 'Bundle or Void',
    route: null
  },
  {
    id: 'CLM-6650-D',
    patient: 'Robert Chen',
    dos: '2026-06-23',
    emr: 'OCHIN Epic',
    billed: '$245.00',
    tier: 'Tier C',
    issue: 'Missing Sliding Fee Schedule',
    action: 'Apply FPL Discount',
    route: null
  }
];

const FqPreSubmissionQueue = () => {
  const { selectedClient } = useOutletContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [queue, setQueue] = useState(mockQueueData);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  const handleSubmitClean = () => {
    setIsSubmitting(true);
    setTimeout(() => {
      setQueue([]);
      setIsSubmitting(false);
    }, 1500);
  };

  const getTierBadge = (tier) => {
    switch (tier) {
      case 'Tier B':
        return <Badge colorScheme="purple" p={1} borderRadius="md"><HStack spacing={1}><Icon as={FiAlertCircle} /><Text>Tier B (LLM)</Text></HStack></Badge>;
      case 'Tier C':
        return <Badge colorScheme="red" p={1} borderRadius="md"><HStack spacing={1}><Icon as={FiAlertCircle} /><Text>Tier C (Manual)</Text></HStack></Badge>;
      default:
        return <Badge>{tier}</Badge>;
    }
  };

  return (
    <Box maxW="7xl" mx="auto">
      <Flex justify="space-between" align="center" mb="6">
        <Box>
          <Heading size="lg" color="blue.700">Pre-Submission Firewall</Heading>
          <Text color="gray.500" mt={1}>
            Automated 837P scrubbing and correction before clearinghouse submission.
          </Text>
        </Box>
      </Flex>

      {/* High-Volume Funnel Visualization */}
      <Box bg={bgColor} shadow="sm" borderRadius="lg" borderWidth="1px" borderColor={borderColor} mb={8} overflow="hidden">
        <Box p={4} bg="blue.50" borderBottomWidth="1px" borderColor={borderColor}>
          <Heading size="sm" color="blue.800">Daily Processing Pipeline</Heading>
        </Box>
        <Box p={6}>
          <SimpleGrid columns={{ base: 1, md: 5 }} spacing={4} alignItems="center">
            <VStack p={4} bg="gray.50" borderRadius="md" borderWidth="1px" borderColor="gray.200">
              <Text fontSize="sm" color="gray.500" fontWeight="bold">Ingested</Text>
              <Text fontSize="3xl" fontWeight="bold">14,250</Text>
              <Text fontSize="xs" color="gray.500">From all EMRs</Text>
            </VStack>
            
            <Flex justify="center"><Icon as={FiArrowRight} color="gray.400" w={6} h={6} /></Flex>
            
            <VStack p={4} bg="green.50" borderRadius="md" borderWidth="1px" borderColor="green.200">
              <Text fontSize="sm" color="green.700" fontWeight="bold">Clean (Passed)</Text>
              <Text fontSize="3xl" fontWeight="bold" color="green.600">10,100</Text>
              <Text fontSize="xs" color="green.600">Ready for Waystar</Text>
            </VStack>

            <Flex justify="center"><Icon as={FiArrowRight} color="gray.400" w={6} h={6} /></Flex>

            <VStack p={4} bg="orange.50" borderRadius="md" borderWidth="1px" borderColor="orange.200">
              <Text fontSize="sm" color="orange.700" fontWeight="bold">Rule Violations</Text>
              <Text fontSize="3xl" fontWeight="bold" color="orange.600">4,150</Text>
              <Text fontSize="xs" color="orange.600">Intercepted</Text>
            </VStack>
          </SimpleGrid>

          <Box mt={6} p={4} bg="blue.50" borderRadius="md" borderWidth="1px" borderColor="blue.200">
            <Flex justify="space-between" align="center">
              <Box>
                <HStack mb={1}>
                  <Icon as={FiSettings} color="blue.600" />
                  <Text fontWeight="bold" color="blue.800">Tier A: Deterministic Auto-Fixes</Text>
                </HStack>
                <Text fontSize="sm" color="blue.700">
                  Silently corrected <b>3,900</b> claims (e.g., missing NPI, invalid Medi-Cal ID format, wrong per-visit code set) without human intervention.
                </Text>
              </Box>
              <VStack align="flex-end">
                <Text fontSize="sm" color="gray.600" fontWeight="bold">Remaining for Tier B/C Review</Text>
                <Badge colorScheme="red" fontSize="lg" px={3} py={1} borderRadius="md">250</Badge>
              </VStack>
            </Flex>
          </Box>
        </Box>
      </Box>

      {/* Filtered Queue Table */}
      <Box bg={bgColor} shadow="sm" borderRadius="lg" borderWidth="1px" borderColor={borderColor} overflow="hidden">
        <Box p="4" borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
          <Flex justify="space-between" align="center">
            <HStack>
              <Icon as={FiFilter} color="gray.500" />
              <Text fontWeight="bold">Filtered View: Manual Review Required</Text>
              <Badge ml={2} colorScheme="gray">Showing 5 of 250</Badge>
            </HStack>
            <Button 
              colorScheme="blue" 
              size="sm"
              leftIcon={<FiSend />} 
              onClick={handleSubmitClean}
              isLoading={isSubmitting}
              loadingText="Submitting..."
              isDisabled={queue.length === 0}
            >
              Submit Fixed Claims
            </Button>
          </Flex>
        </Box>

        <Table variant="simple">
          <Thead bg={headerBg}>
            <Tr>
              <Th>Claim ID</Th>
              <Th>Patient / DOS</Th>
              <Th>Source EMR</Th>
              <Th>Resolution Tier</Th>
              <Th>Scrub Result</Th>
              <Th>Action</Th>
            </Tr>
          </Thead>
          <Tbody>
            {queue.map((claim) => (
              <Tr key={claim.id} _hover={{ bg: hoverBg }}>
                <Td fontWeight="medium">{claim.id}</Td>
                <Td>
                  <Text fontWeight="bold">{claim.patient}</Text>
                  <Text fontSize="sm" color="gray.500">{claim.dos}</Text>
                </Td>
                <Td><Badge variant="outline" colorScheme="gray">{claim.emr}</Badge></Td>
                <Td>{getTierBadge(claim.tier)}</Td>
                <Td>
                  <Text fontSize="sm" fontWeight="bold" color="red.600">
                    {claim.issue}
                  </Text>
                  <Text fontSize="xs" color="gray.500">{claim.action}</Text>
                </Td>
                <Td>
                  <Button 
                    size="sm" 
                    colorScheme={claim.tier === 'Tier B' ? 'purple' : 'blue'} 
                    variant={claim.tier === 'Tier B' ? 'solid' : 'outline'} 
                    onClick={() => {
                      if (claim.route) {
                        navigate(claim.route);
                      } else {
                        // Fallback for non-demo rows
                        navigate('/fq/claim/demo');
                      }
                    }}
                  >
                    {claim.tier === 'Tier B' ? 'Review LLM Fix' : 'Manual Review'}
                  </Button>
                </Td>
              </Tr>
            ))}
            {queue.length === 0 && (
              <Tr>
                <Td colSpan={6} textAlign="center" py={8}>
                  <Icon as={FiCheckCircle} w={10} h={10} color="green.400" mb={3} />
                  <Text fontSize="lg" fontWeight="medium">Queue is empty</Text>
                  <Text color="gray.500">All claims have been processed and submitted.</Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default FqPreSubmissionQueue;
